import React from "react";
import { FaChartPie, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useAppTheme } from "../context/AppThemeContext";
import { getSubscriptionQuota, useTenant } from "../context/TenantContext";

const ResourceQuotaIndicator = ({ resources = [] }) => {
  const { activeTenant } = useTenant();
  const { isDarkMode } = useAppTheme();
  const plan = String(activeTenant?.subscription?.plan || "").trim().toUpperCase();
  const isPremiumPlan = ["PREMIUM", "GROWTH", "PRO", "PROFESSIONAL", "ENTERPRISE"].includes(plan);
  const items = resources
    .map(({ key, label }) => ({ key, label, quota: getSubscriptionQuota(activeTenant?.subscription, key) }))
    .filter(({ quota }) => quota);

  if (isPremiumPlan || !items.length) return null;

  return (
    <section className={`mb-4 overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-slate-700/80 bg-slate-900" : "border-slate-200 bg-white"}`} aria-label="Plan usage limits">
      <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${isDarkMode ? "border-slate-800 bg-slate-900/70" : "border-slate-100 bg-slate-50/70"}`}>
        <div className="flex items-center gap-2.5">
          <span className={`grid h-8 w-8 place-items-center rounded-lg ${isDarkMode ? "bg-cyan-950 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}><FaChartPie /></span>
          <div><p className={`text-sm font-bold ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>Plan usage</p><p className="text-[11px] text-slate-500">Live resource availability</p></div>
        </div>
        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-black tracking-wider text-sky-700">{plan || "FREE"}</span>
      </div>
      <div className={`grid divide-y sm:divide-x sm:divide-y-0 ${items.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"} ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
        {items.map(({ key, label, quota }) => {
          const exhausted = !quota.unlimited && quota.remaining === 0;
          const warning = !quota.unlimited && quota.remaining > 0 && quota.remaining <= 10;
          const percent = quota.unlimited || quota.limit <= 0 ? 100 : Math.min(100, (quota.used / quota.limit) * 100);
          const accent = exhausted ? "bg-rose-500" : warning ? "bg-amber-500" : "bg-cyan-500";
          const valueTone = exhausted ? "text-rose-600" : warning ? "text-amber-600" : isDarkMode ? "text-slate-100" : "text-slate-800";
          return (
            <div key={key} className="px-4 py-3.5">
              <div className="flex items-start justify-between gap-4">
                <div><p className={`text-xs font-semibold ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{label}</p><p className={`mt-0.5 text-lg font-black ${valueTone}`}>{quota.unlimited ? "Unlimited" : `${quota.remaining} remaining`}</p></div>
                {quota.unlimited ? <FaCheckCircle className="mt-1 text-emerald-500" /> : (warning || exhausted) ? <FaExclamationTriangle className={`mt-1 ${exhausted ? "text-rose-500" : "text-amber-500"}`} /> : <span className={`rounded-lg px-2 py-1 text-xs font-bold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>{quota.used}/{quota.limit}</span>}
              </div>
              {!quota.unlimited && <><div className={`mt-3 h-1.5 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}><div className={`h-full rounded-full transition-all duration-500 ${accent}`} style={{ width: `${percent}%` }} /></div><div className="mt-1.5 flex justify-between text-[10px] font-medium text-slate-500"><span>{quota.used} used</span><span>{quota.limit} limit</span></div></>}
              {quota.unlimited && <p className="mt-2 text-[11px] text-slate-500">No resource limit on this plan.</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ResourceQuotaIndicator;
